<script setup>
/**
 * The changes Waslo proposes (spec P-8.3), grouped by the page each lives on: before → after, a
 * tick per change, a link to where it lives, and Apply all or Apply selected. The result is ONE
 * Undo for everything applied.
 *
 * Nothing here is applied until she presses Apply, and Apply sends only the ticks (`{ pick }`).
 * When the picked changes add or re-word an answer, Apply shows the one `teach` it costs and asks
 * first (P-5.12); otherwise it asks nothing. A change set that fails is all or nothing, so the
 * sentence says nothing was changed.
 */
import { computed, ref, watch } from 'vue';

import Button from 'dashboard/components-next/button/Button.vue';
import Checkbox from 'dashboard/components-next/checkbox/Checkbox.vue';
import { frontendURL } from 'dashboard/helper/URLHelper';

import AiConfirmInline from './AiConfirmInline.vue';
import { useAiConfirm } from '../composables/useAiConfirm.js';
import { useWasloI18n } from '../i18n.js';
import { wasloApi, withConfirm } from '../api/client.js';
import {
  changeGroups,
  changePagePath,
  hoursLine,
  pickEmbeds,
} from '../helpers/setup-ai.js';

const props = defineProps({
  accountId: { type: Number, default: null },
  /** A `change_set` proposal (`wasloApi.whatChanged` / `getSetupSuggestions`). */
  suggestion: { type: Object, required: true },
});

const emit = defineEmits(['applied', 'dismissed']);

const { t } = useWasloI18n();
/** E2: a Claude change set says it is Claude's, not Waslo's. */
const introText = computed(() =>
  props.suggestion.source === 'claude'
    ? t('WASLO.CLAUDE.REVIEW.CHANGES_INTRO')
    : t('WASLO.SETUP_AI.CHANGES.INTRO')
);
const price = useAiConfirm({
  accountId: computed(() => props.accountId),
  op: 'apply_answers',
});

const items = computed(() => props.suggestion.changes?.items ?? []);
const picked = ref(items.value.map(item => item.index));
watch(items, value => {
  picked.value = value.map(item => item.index);
});

const busy = ref(false);
const failure = ref('');
const applied = ref(null);
const undone = ref(false);

/** One literal key per action, so a key search and the translation checker see every one. */
const ACTION = {
  'answer.add': () => t('WASLO.SETUP_AI.CHANGES.ACTIONS.ANSWER_ADD'),
  'answer.edit': () => t('WASLO.SETUP_AI.CHANGES.ACTIONS.ANSWER_EDIT'),
  'answer.switch_off': () => t('WASLO.SETUP_AI.CHANGES.ACTIONS.ANSWER_OFF'),
  'personality.edit_line': () => t('WASLO.SETUP_AI.CHANGES.ACTIONS.LINE_EDIT'),
  'task.option_label': () => t('WASLO.SETUP_AI.CHANGES.ACTIONS.OPTION_LABEL'),
  'task.option_remove': () => t('WASLO.SETUP_AI.CHANGES.ACTIONS.OPTION_REMOVE'),
  'task.switch_off': () => t('WASLO.SETUP_AI.CHANGES.ACTIONS.TASK_OFF'),
  'menu.row_label': () => t('WASLO.SETUP_AI.CHANGES.ACTIONS.ROW_LABEL'),
  'menu.row_remove': () => t('WASLO.SETUP_AI.CHANGES.ACTIONS.ROW_REMOVE'),
  'hours.set': () => t('WASLO.SETUP_AI.CHANGES.ACTIONS.HOURS_SET'),
  'notice.add': () => t('WASLO.SETUP_AI.CHANGES.ACTIONS.NOTICE_ADD'),
};

const PAGE = {
  answers: () => t('WASLO.SETUP_AI.CHANGES.PAGES.ANSWERS'),
  personality: () => t('WASLO.SETUP_AI.CHANGES.PAGES.PERSONALITY'),
  tasks: () => t('WASLO.SETUP_AI.CHANGES.PAGES.TASKS'),
  menus: () => t('WASLO.SETUP_AI.CHANGES.PAGES.MENUS'),
  hours: () => t('WASLO.SETUP_AI.CHANGES.PAGES.HOURS'),
  notices: () => t('WASLO.SETUP_AI.CHANGES.PAGES.NOTICES'),
};

const DAY = {
  sun: () => t('WASLO.SETUP_AI.DAYS.SUN'),
  mon: () => t('WASLO.SETUP_AI.DAYS.MON'),
  tue: () => t('WASLO.SETUP_AI.DAYS.TUE'),
  wed: () => t('WASLO.SETUP_AI.DAYS.WED'),
  thu: () => t('WASLO.SETUP_AI.DAYS.THU'),
  fri: () => t('WASLO.SETUP_AI.DAYS.FRI'),
  sat: () => t('WASLO.SETUP_AI.DAYS.SAT'),
};

const shown = (item, text) =>
  item.action === 'hours.set'
    ? hoursLine(text, day => DAY[day](), t('WASLO.SETUP_AI.DAYS.CLOSED'))
    : text;

const groups = computed(() =>
  changeGroups(items.value).map(group => ({
    ...group,
    label: PAGE[group.page](),
  }))
);

/** Where one change lives, so she can open it before she applies it. */
const linkOf = item =>
  props.accountId === null
    ? null
    : frontendURL(changePagePath(props.accountId, item));

const isPicked = index => picked.value.includes(index);
const setPicked = (index, on) => {
  picked.value = on
    ? [...new Set([...picked.value, index])].sort((a, b) => a - b)
    : picked.value.filter(i => i !== index);
};

const applyLabel = pick => {
  const words =
    pick.length === items.value.length
      ? t('WASLO.SETUP_AI.CHANGES.APPLY_ALL')
      : t('WASLO.SETUP_AI.CHANGES.APPLY_SELECTED', { count: pick.length });
  return pickEmbeds(items.value, pick) ? price.label(words) : words;
};

const apply = async pick => {
  if (busy.value || pick.length === 0 || props.accountId === null) return;
  busy.value = true;
  failure.value = '';
  try {
    let result;
    if (pickEmbeds(items.value, pick)) {
      const outcome = await price.confirmAndRun({
        run: credits =>
          wasloApi.applySetupSuggestion(
            props.accountId,
            props.suggestion.id,
            { pick },
            undefined,
            withConfirm(credits)
          ),
      });
      if (outcome.cancelled) return;
      result = outcome.result;
    } else {
      result = await wasloApi.applySetupSuggestion(
        props.accountId,
        props.suggestion.id,
        { pick }
      );
    }
    applied.value = result;
    emit('applied', result);
  } catch (error) {
    failure.value = error?.message || t('WASLO.SETUP_AI.CHANGES.FAILED');
  } finally {
    busy.value = false;
  }
};

const dismiss = async () => {
  if (busy.value || props.accountId === null) return;
  busy.value = true;
  try {
    await wasloApi.dismissSetupSuggestion(props.accountId, props.suggestion.id);
    emit('dismissed');
  } catch (error) {
    failure.value = error?.message || t('WASLO.SETUP_AI.CHANGES.FAILED');
  } finally {
    busy.value = false;
  }
};

const undo = async () => {
  if (busy.value || applied.value === null || props.accountId === null) return;
  busy.value = true;
  try {
    await wasloApi.undoBatch(props.accountId, applied.value.batchId);
    undone.value = true;
  } catch (error) {
    failure.value = error?.message || t('WASLO.SETUP_AI.CHANGES.FAILED');
  } finally {
    busy.value = false;
  }
};
</script>

<template>
  <section
    class="flex flex-col gap-4"
    data-waslo-suggestion-review
    :data-suggestion="suggestion.id"
  >
    <p class="mb-0 text-sm text-n-slate-11">
      {{ introText }}
      <bdi class="text-n-slate-12">{{ suggestion.changes?.text }}</bdi>
    </p>

    <template v-if="applied === null">
      <div
        v-for="group in groups"
        :key="group.page"
        class="flex flex-col gap-2"
        :data-waslo-change-group="group.page"
      >
        <h3 class="mb-0 text-sm font-medium text-n-slate-12">
          {{ group.label }}
        </h3>
        <label
          v-for="item in group.items"
          :key="item.index"
          class="flex items-start gap-3 p-3 rounded-lg outline outline-1 outline-n-weak cursor-pointer"
          :data-waslo-change="item.index"
        >
          <span class="mt-0.5 shrink-0">
            <Checkbox
              :model-value="isPicked(item.index)"
              data-waslo-change-pick
              @update:model-value="on => setPicked(item.index, on)"
            />
          </span>
          <span class="flex flex-col gap-1 text-sm">
            <span class="font-medium text-n-slate-12">
              {{ ACTION[item.action]() }}
            </span>
            <span v-if="item.question" class="text-n-slate-11">
              <bdi>{{ item.question }}</bdi>
            </span>
            <span
              v-if="item.before"
              class="text-n-slate-11 line-through"
              data-waslo-change-before
            >
              <bdi>{{ shown(item, item.before) }}</bdi>
            </span>
            <span
              v-if="item.after"
              class="text-n-slate-12"
              data-waslo-change-after
            >
              <bdi>{{ shown(item, item.after) }}</bdi>
            </span>
            <span v-if="item.endsOn" class="text-xs text-n-slate-11">
              {{ t('WASLO.SETUP_AI.CHANGES.UNTIL', { day: item.endsOn }) }}
            </span>
            <span v-if="item.why" class="text-xs text-n-slate-11">
              <bdi>{{ item.why }}</bdi>
            </span>
            <router-link
              v-if="linkOf(item)"
              :to="linkOf(item)"
              class="text-xs underline text-n-slate-11"
              data-waslo-change-link
            >
              {{ t('WASLO.SETUP_AI.CHANGES.OPEN_PAGE') }}
            </router-link>
          </span>
        </label>
      </div>

      <div class="flex flex-wrap gap-2">
        <Button
          :label="applyLabel(items.map(item => item.index))"
          :disabled="busy"
          data-waslo-changes-apply-all
          @click="apply(items.map(item => item.index))"
        />
        <Button
          v-if="picked.length !== items.length"
          :label="applyLabel(picked)"
          variant="faded"
          color="blue"
          :disabled="busy || picked.length === 0"
          data-waslo-changes-apply-selected
          @click="apply(picked)"
        />
        <Button
          :label="t('WASLO.SETUP_AI.CHANGES.NOT_NOW')"
          variant="faded"
          color="slate"
          :disabled="busy"
          data-waslo-changes-dismiss
          @click="dismiss"
        />
      </div>
      <AiConfirmInline :controller="price" />
    </template>

    <div v-else class="flex flex-wrap items-center gap-2">
      <p class="mb-0 text-sm text-n-slate-12" data-waslo-changes-done>
        {{
          undone
            ? t('WASLO.SETUP_AI.CHANGES.UNDONE')
            : t('WASLO.SETUP_AI.CHANGES.DONE')
        }}
      </p>
      <Button
        v-if="!undone"
        :label="t('WASLO.SETUP_AI.UNDO')"
        variant="faded"
        color="slate"
        size="sm"
        :disabled="busy"
        data-waslo-changes-undo
        @click="undo"
      />
    </div>

    <p
      v-if="failure"
      class="mb-0 text-sm text-n-ruby-11"
      data-waslo-changes-failure
    >
      {{ failure }}
    </p>
  </section>
</template>
